export interface AIStrategyScores {
  roadmapPlanning: number;
  businessAlignment: number;
  implementationReadiness: number;
}

export interface Question {
  id: number;
  text: string;
  options: {
    text: string;
    scores: AIStrategyScores;
  }[];
}

export const questions: Question[] = [
  {
    id: 1,
    text: "How clear is your organization's AI vision and strategy?",
    options: [
      {
        text: "No formal AI strategy - exploring on ad-hoc basis",
        scores: { roadmapPlanning: 3, businessAlignment: 3, implementationReadiness: 2 },
      },
      {
        text: "Basic ideas but nothing documented or structured",
        scores: { roadmapPlanning: 3, businessAlignment: 2, implementationReadiness: 2 },
      },
      {
        text: "Draft strategy exists but not aligned with business goals",
        scores: { roadmapPlanning: 2, businessAlignment: 3, implementationReadiness: 2 },
      },
      {
        text: "Comprehensive AI roadmap tied to business objectives",
        scores: { roadmapPlanning: 0, businessAlignment: 1, implementationReadiness: 1 },
      },
    ],
  },
  {
    id: 2,
    text: "How well do your AI initiatives align with core business objectives?",
    options: [
      {
        text: "AI projects are disconnected from business strategy",
        scores: { roadmapPlanning: 2, businessAlignment: 3, implementationReadiness: 1 },
      },
      {
        text: "Some alignment but mostly technology-driven decisions",
        scores: { roadmapPlanning: 2, businessAlignment: 3, implementationReadiness: 2 },
      },
      {
        text: "Generally aligned but lacking clear ROI metrics",
        scores: { roadmapPlanning: 1, businessAlignment: 2, implementationReadiness: 2 },
      },
      {
        text: "Every AI initiative directly supports business KPIs",
        scores: { roadmapPlanning: 1, businessAlignment: 0, implementationReadiness: 1 },
      },
    ],
  },
  {
    id: 3,
    text: "What's your approach to prioritizing AI opportunities?",
    options: [
      {
        text: "No structured prioritization - pursue what seems interesting",
        scores: { roadmapPlanning: 3, businessAlignment: 2, implementationReadiness: 2 },
      },
      {
        text: "Prioritize based on technology trends and hype",
        scores: { roadmapPlanning: 2, businessAlignment: 3, implementationReadiness: 1 },
      },
      {
        text: "Some criteria but inconsistently applied",
        scores: { roadmapPlanning: 2, businessAlignment: 2, implementationReadiness: 2 },
      },
      {
        text: "Data-driven framework evaluating impact vs. effort",
        scores: { roadmapPlanning: 1, businessAlignment: 1, implementationReadiness: 2 },
      },
    ],
  },
  {
    id: 4,
    text: "How ready is your organization to implement AI solutions?",
    options: [
      {
        text: "Significant gaps in data, skills, and infrastructure",
        scores: { roadmapPlanning: 2, businessAlignment: 1, implementationReadiness: 3 },
      },
      {
        text: "Have some capabilities but major gaps remain",
        scores: { roadmapPlanning: 2, businessAlignment: 2, implementationReadiness: 3 },
      },
      {
        text: "Decent foundation but need to scale capabilities",
        scores: { roadmapPlanning: 1, businessAlignment: 1, implementationReadiness: 2 },
      },
      {
        text: "Strong data, talent, and infrastructure ready to deploy",
        scores: { roadmapPlanning: 1, businessAlignment: 1, implementationReadiness: 0 },
      },
    ],
  },
  {
    id: 5,
    text: "How do you measure success of AI initiatives?",
    options: [
      {
        text: "No clear metrics - rely on qualitative feedback",
        scores: { roadmapPlanning: 2, businessAlignment: 3, implementationReadiness: 1 },
      },
      {
        text: "Track technical metrics but not business impact",
        scores: { roadmapPlanning: 2, businessAlignment: 3, implementationReadiness: 1 },
      },
      {
        text: "Mix of technical and business metrics",
        scores: { roadmapPlanning: 1, businessAlignment: 2, implementationReadiness: 2 },
      },
      {
        text: "Clear ROI metrics tied to business outcomes",
        scores: { roadmapPlanning: 1, businessAlignment: 1, implementationReadiness: 1 },
      },
    ],
  },
  {
    id: 6,
    text: "What's your timeline for AI adoption and scaling?",
    options: [
      {
        text: "No defined timeline - exploring indefinitely",
        scores: { roadmapPlanning: 3, businessAlignment: 2, implementationReadiness: 2 },
      },
      {
        text: "Vague goals without concrete milestones",
        scores: { roadmapPlanning: 3, businessAlignment: 2, implementationReadiness: 2 },
      },
      {
        text: "Timeline exists but frequently shifts",
        scores: { roadmapPlanning: 2, businessAlignment: 2, implementationReadiness: 2 },
      },
      {
        text: "Phased roadmap with clear milestones and deliverables",
        scores: { roadmapPlanning: 0, businessAlignment: 1, implementationReadiness: 2 },
      },
    ],
  },
  {
    id: 7,
    text: "How do you handle change management for AI transformation?",
    options: [
      {
        text: "No formal change management strategy",
        scores: { roadmapPlanning: 2, businessAlignment: 2, implementationReadiness: 3 },
      },
      {
        text: "Basic communication but limited employee buy-in",
        scores: { roadmapPlanning: 1, businessAlignment: 2, implementationReadiness: 3 },
      },
      {
        text: "Active training but cultural resistance remains",
        scores: { roadmapPlanning: 1, businessAlignment: 1, implementationReadiness: 2 },
      },
      {
        text: "Comprehensive program with strong leadership support",
        scores: { roadmapPlanning: 1, businessAlignment: 1, implementationReadiness: 1 },
      },
    ],
  },
];
